/**
 * Product Recommendations Component
 * Handles product recommendations on the main product page
 */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.swiperInstance = null;
    this.gridQuery = null;
    this.onGridQueryChange = null;
  }

  /**
   * True while the section should render as a paged multi-row grid instead of a
   * single-row carousel: the merchant picked the grid layout and the viewport is
   * below the section's desktop breakpoint (64rem).
   */
  get isMobileGrid() {
    if (this.dataset.mobileLayout !== "grid") {
      return false;
    }

    if (!this.gridQuery) {
      this.gridQuery = window.matchMedia("(width < 64rem)");
    }

    return this.gridQuery.matches;
  }

  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  /**
   * Re-runs the carousel/grid decision when the viewport crosses 64rem, so a
   * resize does not leave a destroyed Swiper or a grid under an active one.
   */
  watchGridBreakpoint(sectionId) {
    if (this.dataset.mobileLayout !== "grid" || this.onGridQueryChange) {
      return;
    }

    if (!this.gridQuery) {
      this.gridQuery = window.matchMedia("(width < 64rem)");
    }

    // The mobile grid and the desktop carousel need different Swiper options
    // (grid rows, slides per view), and `grid` cannot be changed through
    // breakpoints without a re-init — so rebuild the instance on each crossing.
    this.onGridQueryChange = () => {
      this.initializeSwiper(sectionId);
    };

    this.gridQuery.addEventListener("change", this.onGridQueryChange);
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const target = this.dataset.target;
    const limit = this.dataset.limit || 4;
    const intent = this.dataset.intent || "related";

    // Check if recommendations already rendered server-side
    const existingContainer = this.querySelector(`[class*="${target}"]`);

    if (existingContainer && existingContainer.innerHTML.trim().length > 0) {
      console.log("Server-rendered recommendations found, initializing Swiper");
      this.initializeSwiper(sectionId);
      return;
    }

    if (!productId) {
      console.error("Product ID is required for recommendations");
      return;
    }

    this.fetchRecommendations(productId, sectionId, target, limit, intent);
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.gridQuery && this.onGridQueryChange) {
      this.gridQuery.removeEventListener("change", this.onGridQueryChange);
      this.onGridQueryChange = null;
    }

    this.destroySwiper();
  }

  async fetchRecommendations(productId, sectionId, target, limit, intent) {
    this.abortController = new AbortController();

    try {
      const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=${intent}`;

      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");

      const recommendations = html.querySelector(`.${target}`);

      if (recommendations && recommendations.innerHTML.trim().length > 0) {
        this.innerHTML = recommendations.innerHTML;
        this.initializeSwiper(sectionId);
      } else {
        console.log("No recommendations found for product:", productId);
        this.style.display = "none";

        this.dispatchEvent(
          new CustomEvent("recommendations:empty", {
            bubbles: true,
            detail: { productId },
          }),
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      console.error("Error fetching product recommendations:", error);
      this.style.display = "none";

      this.dispatchEvent(
        new CustomEvent("recommendations:error", {
          bubbles: true,
          detail: { error, productId },
        }),
      );
    }
  }

  initializeSwiper(sectionId) {
    this.watchGridBreakpoint(sectionId);

    const mobileGrid = this.isMobileGrid;

    requestAnimationFrame(() => {
      const swiperEl = this.querySelector(`#swiper-${sectionId}`);
      const prevButton = this.querySelector(".carousel-button-prev");
      const nextButton = this.querySelector(".carousel-button-next");
      const pagination = this.querySelector(".carousel-progressbar");

      if (!swiperEl) {
        console.warn("Swiper element not found");
        return;
      }

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }

      const options = {
        slidesPerView: 1.3,
        spaceBetween: 16,
        navigation: {
          prevEl: prevButton,
          nextEl: nextButton,
        },
        pagination: {
          el: pagination,
          type: "progressbar",
        },
        breakpoints: {
          768: {
            slidesPerView: 2.3,
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 32,
          },
        },
      };

      if (mobileGrid) {
        // Figma 72:665 — 2 columns x N rows per page, 24px gutters, paging to
        // the right. `slidesPerGroup` must equal the visible column count so a
        // page advances by a whole screen instead of a single column.
        const columns = parseInt(this.dataset.mobileColumns, 10) || 2;
        const rows = parseInt(this.dataset.mobileRows, 10) || 2;

        options.slidesPerView = columns;
        options.slidesPerGroup = columns;
        options.spaceBetween = 24;
        options.grid = { rows: rows, fill: "row" };
        // Desktop options live in a separate instance built on breakpoint
        // crossing; leaving them here would apply a stale grid above 64rem.
        delete options.breakpoints;
      }

      this.swiperInstance = new Swiper(swiperEl, options);
    });
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
